/**
 * GBI Analytics — GA4 + Meta Pixel event helpers
 *
 * Usage:
 *   GBI.Analytics.viewItem(product)
 *   GBI.Analytics.addToCart(product, quantity)
 *   GBI.Analytics.viewItemList(items, listName)
 *   GBI.Analytics.beginCheckout(cart)
 *   GBI.Analytics.purchase(order)
 *
 * Each method safely checks for the existence of gtag / fbq before firing.
 */

window.GBI = window.GBI || {};

GBI.Analytics = {
  /* ---- internal helpers ---- */

  _hasGA4: function () {
    return typeof window.gtag === 'function';
  },

  _hasFBQ: function () {
    return typeof window.fbq === 'function';
  },

  /**
   * Build a GA4 item object from a product-like hash.
   *
   * Expected shape (mirrors Shopify product Liquid → JS):
   *   { id, title, vendor, price, variant_id, variant_title, sku, quantity }
   */
  _ga4Item: function (product, quantity) {
    return {
      item_id: product.variant_id || product.id,
      item_name: product.title,
      item_brand: product.vendor || '',
      item_variant: product.variant_title || '',
      price: parseFloat(product.price) || 0,
      quantity: quantity || 1
    };
  },

  /* ---- public API ---- */

  /**
   * View a single product (PDP).
   *
   * GA4  → view_item
   * Meta → ViewContent
   */
  viewItem: function (product) {
    try {
      if (this._hasGA4()) {
        gtag('event', 'view_item', {
          currency: product.currency || 'USD',
          value: parseFloat(product.price) || 0,
          items: [this._ga4Item(product, 1)]
        });
      }

      if (this._hasFBQ()) {
        fbq('track', 'ViewContent', {
          content_ids: [product.variant_id || product.id],
          content_name: product.title,
          content_type: 'product',
          value: parseFloat(product.price) || 0,
          currency: product.currency || 'USD'
        });
      }
    } catch (e) { /* analytics must never throw */ }
  },

  /**
   * Add to cart.
   *
   * GA4  → add_to_cart
   * Meta → AddToCart
   */
  addToCart: function (product, quantity) {
    try {
      var qty = quantity || 1;
      var value = (parseFloat(product.price) || 0) * qty;

      if (this._hasGA4()) {
        gtag('event', 'add_to_cart', {
          currency: product.currency || 'USD',
          value: value,
          items: [this._ga4Item(product, qty)]
        });
      }

      if (this._hasFBQ()) {
        fbq('track', 'AddToCart', {
          content_ids: [product.variant_id || product.id],
          content_name: product.title,
          content_type: 'product',
          value: value,
          currency: product.currency || 'USD'
        });
      }
    } catch (e) { /* analytics must never throw */ }
  },

  /**
   * View a list of products (collection / search results).
   *
   * GA4  → view_item_list
   * Meta → ViewCategory
   */
  viewItemList: function (items, listName) {
    try {
      var self = this;

      if (this._hasGA4()) {
        var ga4Items = items.map(function (item, index) {
          var mapped = self._ga4Item(item, 1);
          mapped.index = index;
          mapped.item_list_name = listName || '';
          return mapped;
        });

        gtag('event', 'view_item_list', {
          item_list_name: listName || '',
          items: ga4Items
        });
      }

      if (this._hasFBQ()) {
        fbq('track', 'ViewCategory', {
          content_name: listName || '',
          content_type: 'product_group',
          content_ids: items.map(function (item) {
            return item.variant_id || item.id;
          })
        });
      }
    } catch (e) { /* analytics must never throw */ }
  },

  /**
   * Begin checkout.
   *
   * GA4  → begin_checkout
   * Meta → InitiateCheckout
   */
  beginCheckout: function (cart) {
    try {
      var self = this;

      if (this._hasGA4()) {
        var ga4Items = (cart.items || []).map(function (item) {
          return self._ga4Item(item, item.quantity || 1);
        });

        gtag('event', 'begin_checkout', {
          currency: cart.currency || 'USD',
          value: parseFloat(cart.total_price) || 0,
          items: ga4Items
        });
      }

      if (this._hasFBQ()) {
        fbq('track', 'InitiateCheckout', {
          content_ids: (cart.items || []).map(function (item) {
            return item.variant_id || item.id;
          }),
          content_type: 'product',
          num_items: (cart.items || []).length,
          value: parseFloat(cart.total_price) || 0,
          currency: cart.currency || 'USD'
        });
      }
    } catch (e) { /* analytics must never throw */ }
  },

  /**
   * Purchase / order complete.
   *
   * GA4  → purchase
   * Meta → Purchase
   */
  /**
   * Email popup events: impression, dismiss, dismiss_permanent, submit.
   *
   * GA4  → popup_interaction (custom event)
   * Meta → (not tracked — no standard event)
   */
  trackPopup: function (action) {
    try {
      if (this._hasGA4()) {
        gtag('event', 'popup_interaction', {
          popup_type: 'email',
          popup_action: action
        });
      }
    } catch (e) { /* analytics must never throw */ }
  },

  purchase: function (order) {
    try {
      var self = this;

      if (this._hasGA4()) {
        var ga4Items = (order.items || order.line_items || []).map(function (item) {
          return self._ga4Item(item, item.quantity || 1);
        });

        gtag('event', 'purchase', {
          transaction_id: order.order_number || order.id,
          currency: order.currency || 'USD',
          value: parseFloat(order.total_price) || 0,
          shipping: parseFloat(order.shipping_price) || 0,
          tax: parseFloat(order.tax_price) || 0,
          items: ga4Items
        });
      }

      if (this._hasFBQ()) {
        fbq('track', 'Purchase', {
          content_ids: (order.items || order.line_items || []).map(function (item) {
            return item.variant_id || item.id;
          }),
          content_type: 'product',
          num_items: (order.items || order.line_items || []).length,
          value: parseFloat(order.total_price) || 0,
          currency: order.currency || 'USD'
        });
      }
    } catch (e) { /* analytics must never throw */ }
  }
};
